import { Box, Button, Typography, Grid, Chip } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { useState } from "react";
import { EditSectionModal } from "./EditSectionModal";

const ReviewStep = () => {
  const dispatch = useAppDispatch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState("");

  const {
    title,
    level,
    tags,
    description,
    responsibilities,
    skills,
    payType,
    payAmount,
    payAmountMax,
    jobType,
    payRate,
    duration,
    durationType,
    primaryLanguage,
    questions,
    workSite,
    specialization,
    deliverables,
  } = useAppSelector((state) => state.jobPostCreation);

  const handleEdit = (section: string) => {
    setCurrentSection(section);
    setEditModalOpen(true);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={4}>
        Review Your Job Post
      </Typography>

      <Grid container spacing={4}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Basic Information
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={500}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Level: {level}
                </Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  {tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleEdit("title")}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Description & Requirements
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="body1" whiteSpace="pre-wrap">
                  {description}
                </Typography>
                {responsibilities.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                      Responsibilities:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {responsibilities.map((item, index) => (
                        <li key={index}>
                          <Typography variant="body1">{item}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                {skills.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                      Qualifications:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {skills.map((skill, index) => (
                        <li key={index}>
                          <Typography variant="body1">{skill}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleEdit("description")}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Deliverables - Only show for Short-term jobs */}
        {jobType === "Short-term" && (
          <Grid item xs={12}>
            <Box mb={3}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Deliverables
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  {deliverables.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {deliverables.map((deliverable, index) => (
                        <li key={index}>
                          <Typography variant="body1">{deliverable}</Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No deliverables specified
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleEdit("deliverables")}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Budget & Duration */}
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Budget & Duration
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="body1">
                  <strong>Job Type:</strong> {jobType}
                </Typography>
                <Typography variant="body1" mt={1}>
                  <strong>Payment Type:</strong> {payType}
                </Typography>
                <Typography variant="body1" mt={1}>
                  <strong>Budget:</strong> ${Number(payAmount).toLocaleString()}
                  {payType === "Range" &&
                    ` - $${Number(payAmountMax).toLocaleString()}`}
                </Typography>
                {jobType !== "Short-term" && (
                  <Typography variant="body1" mt={1}>
                    <strong>Payment Frequency:</strong> {payRate}
                  </Typography>
                )}
                {jobType === "Internship" && (
                  <Typography variant="body1" mt={1}>
                    <strong>Duration:</strong> {duration} {durationType}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleEdit("payType")}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Box mb={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Additional Information
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                {primaryLanguage && (
                  <Typography variant="body1">
                    <strong>Primary Language:</strong> {primaryLanguage}
                  </Typography>
                )}
                {workSite && (
                  <Typography variant="body1" mt={1}>
                    <strong>Work Site:</strong> {workSite}
                  </Typography>
                )}
                {specialization && (
                  <Typography variant="body1" mt={1}>
                    <strong>Specialization:</strong> {specialization}
                  </Typography>
                )}
                {questions.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                      Screening Questions:
                    </Typography>
                    <ol style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {questions.map((question, index) => (
                        <li key={index}>
                          <Typography variant="body1">{question}</Typography>
                        </li>
                      ))}
                    </ol>
                  </Box>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleEdit("additional")}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <EditSectionModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        section={currentSection}
      />
    </Box>
  );
};

export default ReviewStep; 